import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { formatAddress } from "../../../components/Cards/JobHistoryCard";
import SearchBar from "../../../components/Filters/SearchBarWithFilters";
import HeaderBar from "../../../components/Header/HeaderBar";
import useJobHistoryReport from "../../../hooks/useJobHistoryReport";
import usePropertyHistoryFilterAutocomplete from "../../../hooks/usePropertyHistoryFilterAutocomplete";
import useAdminCompanyOptions from "../../../hooks/useAdminCompanyOptions";
import axiosJobHistory from "../../../services/axios/jobHistory";
import PropertyHistoryFilters from "../../../components/Filters/PropertyHistoryFilters";
import InfinitePageLoader from "../../../components/InfinitePageLoader";
import FlagRed from "../../../resources/flag-red.svg";
import { useInfiniteQuery } from "@tanstack/react-query";
import PulseLoader from "react-spinners/PulseLoader";
import servicedByLabel from "../../../utils/servicedByLabel";
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}
function buildQueryParamString(filters, searchTerm) {
  let cleanedUp = Object.keys(filters)
    .filter(
      (key) =>
        filters[key] != null &&
        (!Array.isArray(filters[key]) || filters[key].length > 0),
    )
    .reduce((obj, key) => {
      obj[key] = filters[key];
      return obj;
    }, {});

  // Handle date range manually:
  delete cleanedUp.dateRange;
  if (filters.dateRange) {
    cleanedUp.firstDate = filters.dateRange?.firstDate.toISOString();
    cleanedUp.secondDate = filters.dateRange?.secondDate.toISOString();
  }
  if (searchTerm) cleanedUp.searchTerm = searchTerm;
  if (cleanedUp.companies === undefined || cleanedUp.companies?.length === 0) {
    cleanedUp.companies = "all";
  }
  let queryParams = new URLSearchParams(cleanedUp).toString();
  return queryParams;
}
/**
 * Get a filter object from the query parameters in the url
 * @param {URLSearchParams} queryParams
 */
function getFilterArray(queryParams, currentCompanyId) {
  let obj = {};
  let arrays = [
    "cities",
    "jobs",
    "services",
    "operators",
    "routes",
    "companies",
    "servicedBy",
  ];
  let simpleValues = ["season"];
  let booleanValues = ["flaggedForReview"];
  for (const key of arrays) {
    if (queryParams.has(key)) {
      obj[key] = queryParams.get(key).split(",");
    }
  }
  for (const key of simpleValues) {
    if (queryParams.has(key)) {
      obj[key] = queryParams.get(key);
    }
  }
  for (const key of booleanValues) {
    if (queryParams.has(key)) {
      obj[key] = queryParams.get(key) === "true";
    }
  }
  if (queryParams.has("firstDate") && queryParams.has("secondDate")) {
    obj["dateRange"] = {
      firstDate: new Date(queryParams.get("firstDate")),
      secondDate: new Date(queryParams.get("secondDate")),
    };
  }
  if (obj["servicedBy"] == null) {
    obj["servicedBy"] = [];
  }
  // `companies` is the access scope rather than a filter the user sets. It is
  // derived from `servicedBy` whenever the selection changes and round-tripped
  // through the url so that back / forward restore the same scope.
  if (obj["companies"] == null) {
    obj["companies"] = [currentCompanyId];
  }
  if (obj["companies"].includes("all")) {
    obj["companies"] = [];
  }
  return obj;
}
const AdminJobHistoryPage = (props) => {
  let queryParams = useQuery();
  let queryFilters = getFilterArray(
    queryParams,
    props.currentUser.currentCompanyId,
  );
  const navigate = useNavigate();

  const [currentFilter, setCurrentFilter] = useState(queryFilters);
  const [currentSearchTerm, setCurrentSearchTerm] = useState(
    queryParams.has("searchTerm") ? queryParams.get("searchTerm") : null,
  );
  const [perPage] = useState(30);

  const selectedSingleCompany =
    currentFilter.companies?.length === 1 ? currentFilter.companies[0] : null;

  const updateFilters = async (newFilters, searchTerm = currentSearchTerm) => {
    let queryParams = buildQueryParamString(newFilters, searchTerm);
    navigate("/admin/property/history?" + queryParams, {}); // update the url query so that back / forward work

    setCurrentFilter(newFilters);
    setCurrentSearchTerm(searchTerm);
  };

  const { searchInput, setSearchInput, suggestions, onSelectSuggestion } =
    usePropertyHistoryFilterAutocomplete({
      companyId: selectedSingleCompany,
      filters: currentFilter,
      initialSearchInput: currentSearchTerm ?? "",
      onApplyFilters: (newFilters) => {
        updateFilters(newFilters, null);
      },
    });

  // Used to resolve each row's Serviced By label below. The export does not need
  // it: a form post cannot carry an empty array, and the report endpoint reads an
  // absent company list the same way the list endpoint reads an empty one, so
  // both end up scoped to every company the user administers.
  const { companyIds: adminCompanyIds } = useAdminCompanyOptions(
    props.currentUser,
  );
  const { createJobHistoryReport } = useJobHistoryReport({
    companies: currentFilter.companies,
    dateRange: {
      startDate: currentFilter.dateRange?.firstDate,
      endDate: currentFilter.dateRange?.secondDate,
    },
    services: currentFilter.services,
    jobs: currentFilter.jobs,
    cities: currentFilter.cities,
    flaggedForReview: currentFilter.flaggedForReview,
    season: currentFilter.season,
    searchPhrase: currentSearchTerm,
    operators: currentFilter.operators,
    routes: currentFilter.routes,
    servicedBy: currentFilter.servicedBy,
  });

  // This is the primary data query:
  const query = useInfiniteQuery({
    queryKey: ["jobs", currentFilter, currentSearchTerm],
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      lastPage.length < perPage ? null : lastPageParam + 1,
    queryFn: async ({ pageParam }) => {
      const promise = axiosJobHistory.getAllJobHistoryV2(
        props.currentUser.currentCompanyId,
        currentSearchTerm,
        currentFilter,
        perPage,
        pageParam * perPage,
      );
      return await promise;
    },
  });

  useDidUpdateEffect(() => {
    updateFilters({
      ...currentFilter,
      companies: [props.currentUser.currentCompanyId],
      // The previous company's service partners are not offered under the new
      // one, so a stale selection would filter everything out.
      servicedBy: [],
    });
  }, [props.currentUser.currentCompanyId]);

  const onSearch = async (searchTerm) => {
    let queryParams = buildQueryParamString(currentFilter, searchTerm);
    navigate("/admin/property/history?" + queryParams, {}); // update the url query so that back / forward work

    setCurrentSearchTerm(searchTerm);
    setSearchInput(searchTerm ?? "");
  };

  return (
    <>
      <div className="flex flex-col items-center">
        <HeaderBar
          title="Property History"
          buttons={[
            {
              color: "blue",
              label: "Export",
              onClick: createJobHistoryReport,
            },
          ]}
        />
        <div className="w-full px-1 lg:px-10">
          <SearchBar
            onSearch={onSearch}
            value={searchInput}
            onChange={setSearchInput}
            suggestions={suggestions}
            onSelectSuggestion={onSelectSuggestion}
          >
            <PropertyHistoryFilters
              onSubmitFilters={(newFilters) => updateFilters(newFilters)}
              filters={currentFilter}
            />
            {currentFilter.companies?.length !== 1 ? (
              <p className="text-gray-500 text-xs">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-4 mr-1 inline"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                  />
                </svg>
                Some filters are disabled unless you filter by a single company
                you administer.
              </p>
            ) : null}
          </SearchBar>
          {query.isPending ? (
            <PulseLoader
              color={"#cccccc"}
              size={15}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="text-center mt-20"
            />
          ) : (
            <>
              <InfinitePageLoader
                loadNextPage={query.fetchNextPage}
                hasMorePages={query.hasNextPage}
                loading={query.isFetchingNextPage}
              >
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-[16px]">
                        <img
                          width="40"
                          height="40"
                          className="w-4 h-4 max-w-none"
                          src={FlagRed}
                          alt="Needs Review"
                        />
                      </th>
                      <th>Property</th>
                      {currentFilter.companies?.length === 1 ? null : (
                        <th>Company</th>
                      )}
                      <th>
                        <span className="hidden lg:inline">Hours On Site</span>
                        <span className="lg:hidden">Hours</span>
                      </th>
                      <th className="hidden lg:table-cell">Start / Finish</th>
                      <th>Serviced By</th>
                      <th className="hidden lg:table-cell">
                        Services Performed
                      </th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data?.pages?.map((p) =>
                      p.map((job) => (
                        <tr
                          key={job._id}
                          onClick={() =>
                            navigate(`/admin/property/history/${job._id}`)
                          }
                          className="cursor-pointer"
                        >
                          <td className="w-[16px]">
                            {job.flaggedForReview ? (
                              <img
                                width="40"
                                height="40"
                                className="w-4 h-4 max-w-none"
                                src={FlagRed}
                                alt="Needs Review"
                              />
                            ) : null}
                          </td>
                          <td>
                            <span>{job.name}</span>
                            <div className="sub">{formatAddress(job)}</div>
                          </td>
                          {currentFilter.companies?.length === 1 ? null : (
                            <td>{job.companyName ?? ""}</td>
                          )}
                          <td>{hoursOnSite(job)}</td>
                          <td className="text-sm hidden lg:table-cell">
                            <div>{formatTime(job.startTimeUTC)}</div>
                            <div>{formatTime(job.endTimeUTC)}</div>
                          </td>
                          <td>
                            {servicedByLabel(job, [
                              props.currentUser.currentCompanyId,
                              ...adminCompanyIds,
                            ])}
                          </td>
                          <td className="sub max-w-[100px] text-xs hidden lg:table-cell">
                            {serviceList(job).join(", ")}
                          </td>
                          <td className="underline">
                            <Link to={`/admin/property/history/${job._id}`}>
                              View
                            </Link>
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </InfinitePageLoader>
            </>
          )}
        </div>
      </div>
    </>
  );
};

function formatTime(dateString) {
  if (dateString) {
    let date = new Date(dateString);
    return format(date, "MMM d h:mm bbb");
  }
  if (dateString === undefined) {
    return "In progress";
  }
  return "";
}

function serviceList(job) {
  let r = [];
  for (const key in job.servicesPerformed ?? {}) {
    let s = job.servicesPerformed[key];
    if (s.checked) {
      r.push(s.label);
    }
  }
  return r;
}

function hoursOnSite(record) {
  if (record.startTimeUTC && record.endTimeUTC) {
    let endTimeUTC = new Date(record.endTimeUTC) ?? new Date();
    return (
      Math.floor(
        ((endTimeUTC - new Date(record.startTimeUTC).getTime()) /
          1000 /
          60 /
          60) *
          10,
      ) / 10
    );
  }
  return "";
}

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminJobHistoryPage);

function useDidUpdateEffect(fn, inputs) {
  const isMountingRef = useRef(false);

  useEffect(() => {
    isMountingRef.current = true;
  }, []);

  useEffect(() => {
    if (!isMountingRef.current) {
      return fn();
    } else {
      isMountingRef.current = false;
    }
  }, inputs);
}
